import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgramVersionItemDto } from '../types/dtos/program/ProgramVersionItemDto';
import { createProgramVersionItemService } from '../services/program-version-item.service';
import { useAllProgramVersionsDetailed } from './program.hooks';
import { ProgramVersionDto } from '../types/dtos/program';

const PROGRAM_VERSION_ITEMS_KEY = 'program-version-items';

export const useProgramVersionItems = (
  organizationId: string,
  programId: string,
  versionId: string,
  isActive?: boolean
) => {
  return useQuery({
    queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId, { isActive }],
    queryFn: () => {
      const service = createProgramVersionItemService(organizationId, programId, versionId);
      return service.findAll({ isActive }).then((res) => res.data);
    },
    enabled: !!organizationId && !!programId && !!versionId,
  });
};

export const useProgramVersionItem = (
  organizationId: string,
  programId: string,
  versionId: string,
  id: string
) => {
  return useQuery({
    queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId, id],
    queryFn: () => {
      const service = createProgramVersionItemService(organizationId, programId, versionId);
      return service.findById(id).then((res) => res.data);
    },
    enabled: !!organizationId && !!programId && !!versionId && !!id,
  });
};

export const useCreateProgramVersionItem = (
  organizationId: string,
  programId: string,
  versionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: ProgramVersionItemDto) => {
      const service = createProgramVersionItemService(organizationId, programId, versionId);
      return service.create(item).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId],
      });
    },
  });
};

export const useUpdateProgramVersionItem = (
  organizationId: string,
  programId: string,
  versionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, item }: { id: string; item: ProgramVersionItemDto }) => {
      const service = createProgramVersionItemService(organizationId, programId, versionId);
      return service.update(id, item).then((res) => res.data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId, id],
      });
    },
  });
};

export const useDeleteProgramVersionItem = (
  organizationId: string,
  programId: string,
  versionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      const service = createProgramVersionItemService(organizationId, programId, versionId);
      return service.delete(id).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_VERSION_ITEMS_KEY, organizationId, programId, versionId],
      });
    },
  });
};

/**
 * Fetches all active program version items across all programs and versions
 * for the given organization. Composes usePrograms + useAllProgramVersionsDetailed
 * + batch fetches items per version. Used by Report Builder template mode to
 * avoid the heavy challenges table request.
 */
export const useAllProgramVersionItems = (organizationId: string) => {
  const { data: programVersions = [] } = useAllProgramVersionsDetailed(organizationId);

  return useQuery<ProgramVersionItemDto[], Error>({
    queryKey: [
      'allProgramVersionItems',
      organizationId,
      programVersions?.map((v: ProgramVersionDto) => v.id).join(','),
    ],
    queryFn: async () => {
      if (!programVersions || programVersions.length === 0) return [];

      const versionsByProgram = new Map<string, ProgramVersionDto[]>();
      for (const version of programVersions) {
        const list = versionsByProgram.get(version.programId) || [];
        list.push(version);
        versionsByProgram.set(version.programId, list);
      }

      const allItems = await Promise.all(
        Array.from(versionsByProgram.entries()).flatMap(([programId, versions]) =>
          versions.map((version) => {
            const service = createProgramVersionItemService(organizationId, programId, version.id);
            return service.findAll({ isActive: true }).then((res) => res.data || []);
          })
        )
      );

      return allItems.flat();
    },
    enabled: !!organizationId && !!programVersions && programVersions.length > 0,
  });
}; 